import Plyr from 'plyr';
import 'plyr/dist/plyr.css';
import Hls from 'hls.js';

/**
 * LSPlyr - Advanced video player manager with HLS support
 * Uses Plyr.io for UI and HLS.js for streaming
 */
class LSPlyr {
    /**
     * Constructor for the LSPlyr class
     * @param {Object} config - Global configuration options
     */
    constructor(config = {}) {
        // Store active player instances
        this.players = [];

        // Default global configuration
        this.defaultConfig = {
            autoInitialize: true,
            observeDom: true,
            selector: '#ls_video_player',
            hlsConfig: {
                maxBufferLength: 30,
                maxMaxBufferLength: 60,
                enableWorker: true,
                fragLoadingMaxRetry: 5,
                manifestLoadingMaxRetry: 3,
                levelLoadingMaxRetry: 3,
                debug: false
            },
            plyrConfig: {
                controls: [
                    'play-large',
                    'play',
                    'progress',
                    'current-time',
                    'duration',
                    'mute',
                    'volume',
                    'settings',
                    'fullscreen',
                ],
                settings: ['quality', 'speed'],
                quality: {
                    default: -1,
                    options: [],
                    forced: true,
                },
                speed: {
                    selected: 1,
                    options: [0.5, 0.75, 1, 1.25, 1.5, 2]
                }
            },
            // Advertisement configuration
            adConfig: {
                enabled: true,
                prerollEnabled: true,
                midrollEnabled: true,
                postrollEnabled: false,
                adFrequency: 5,
                vastAdUrls: {
                    preroll: [],
                    midroll: [],
                    postroll: [],
                    any: []
                },
                htmlAds: {
                    preroll: [],
                    midroll: [],
                    postroll: [],
                    any: []
                },
                imaSettings: {
                    autoPlayAdBreaks: true,
                    vpaidMode: 2,
                    disableCustomPlaybackForIOS10Plus: true,
                    numRedirects: 5
                }
            }
        };

        // Merge provided config with defaults
        this.config = this.mergeDeep(this.defaultConfig, config);

        // Initialize players if autoInitialize is true
        if (this.config.autoInitialize) {
            this.init();
        }

        // Set up event handlers
        this.setupEventHandlers();

        // Register custom Go to Live control
        this.registerGoToLiveControl();
    }

    /**
     * Register custom Go to Live control with Plyr
     */
    registerGoToLiveControl() {
        if (!Plyr) {
            console.error('[LSPlyr] Plyr is not available');
            return;
        }

        // Only register once
        if (Plyr.prototype.goToLivePoint) return;

        // Add goToLivePoint method to Plyr prototype
        Plyr.prototype.goToLivePoint = function () {
            if (this.source && this.source.type === 'application/x-mpegURL') {
                const playerItem = window.LSPlyrInstance?.players.find(
                    item => item.player === this && item.hls
                );

                if (playerItem && playerItem.hls) {
                    // Jump to live edge
                    this.currentTime = 1000000000;

                    if (this.paused) {
                        this.play();
                    }

                    // Dispatch custom event
                    const event = new CustomEvent('ls-player:go-to-live', {
                        detail: { player: this }
                    });

                    if (this.elements && this.elements.container) {
                        this.elements.container.dispatchEvent(event);
                    }
                    document.dispatchEvent(event);

                    return true;
                }
            }

            // Fallback for non-HLS streams
            if (this.duration) {
                this.currentTime = this.duration;

                if (this.paused) {
                    this.play();
                }

                return true;
            }

            return false;
        };
    }

    /**
     * Initialize the LSPlyr system
     */
    init() {
        // Prioritize initializing ls_video_player if it exists
        const lsVideoPlayer = document.getElementById('ls_video_player');
        if (lsVideoPlayer) {
            this.initSinglePlayer(lsVideoPlayer);
        }

        // Initialize remaining existing players
        this.initExistingPlayers();

        // Set up DOM observer if enabled
        if (this.config.observeDom) {
            this.setupDomObserver();
        }

        return this;
    }

    /**
     * Initialize all existing player elements on the page
     */
    initExistingPlayers() {
        const players = document.querySelectorAll(`${this.config.selector}:not(.ls-player-initialized):not(#ls_video_player)`);
        if (players.length > 0) {
            this.initPlayers(players);
        }
        return this;
    }

    /**
     * Set up mutation observer to watch for new player elements
     */
    setupDomObserver() {
        const observer = new MutationObserver((mutations) => {
            const lsVideoPlayer = document.getElementById('ls_video_player');
            if (lsVideoPlayer && !lsVideoPlayer.classList.contains('ls-player-initialized')) {
                this.initSinglePlayer(lsVideoPlayer);
            }

            const newPlayers = document.querySelectorAll(
                `${this.config.selector}:not(.ls-player-initialized)`
            );

            if (newPlayers.length > 0) {
                this.initPlayers(newPlayers);
            }
        });

        observer.observe(document.body, {
            childList: true,
            subtree: true,
        });

        this.observer = observer;
        return this;
    }

    /**
     * Initialize player instances for a collection of elements
     */
    initPlayers(videoElements) {
        videoElements.forEach((videoElement) => {
            this.initSinglePlayer(videoElement);
        });

        return this;
    }

    /**
     * Initialize a single player instance
     */
    initSinglePlayer(videoElement) {
        if (videoElement.classList.contains('ls-player-initialized')) {
            return null;
        }

        const videoSrc = videoElement.getAttribute('data-hls-url') || videoElement.getAttribute('src');
        const isLive = videoElement.getAttribute('data-is-live') === 'true' ||
            (videoSrc && videoSrc.endsWith('.m3u8'));

        if (!videoSrc) {
            console.warn('[LSPlyr] Missing video source URL');
            return null;
        }

        // Parse element-specific configuration
        let elementConfig = {};
        const configAttr = videoElement.getAttribute('data-plyr');
        if (configAttr) {
            try {
                elementConfig = JSON.parse(configAttr);
            } catch (e) {
                console.warn('[LSPlyr] Invalid data-plyr JSON:', e);
            }
        }

        // Add Go to Live control for live streams
        if (isLive && elementConfig.controls && !elementConfig.controls.includes('go-to-live')) {
            if (!Array.isArray(elementConfig.controls)) {
                elementConfig.controls = this.config.plyrConfig.controls.slice();
            }
            elementConfig.controls.push('go-to-live');
        }

        const finalConfig = this.mergeDeep({}, this.config.plyrConfig, elementConfig);
        let player = null;

        // Handle HLS streams
        if (videoSrc.endsWith('.m3u8')) {
            player = this.initHlsPlayer(videoElement, videoSrc, finalConfig);
        } else {
            // Standard video format
            videoElement.src = videoSrc;
            player = new Plyr(videoElement, finalConfig);
            this.setupPlayerEvents(player, videoElement);
            this.players.push({
                element: videoElement,
                player: player,
                hls: null,
                isLive: false
            });

            const event = new CustomEvent('ls-player:initialized', {
                detail: { player, element: videoElement, isLive: false }
            });
            document.dispatchEvent(event);
        }

        videoElement.classList.add('ls-player-initialized');
        videoElement.setAttribute('data-ls-player-id', this.players.length - 1);

        return player;
    }

    /**
 * Initialize HLS player with proper error handling
 */
    initHlsPlayer(videoElement, videoSrc, config) {
        // Validate inputs
        if (!videoElement) {
            console.error('[LSPlyr] Video element is required');
            return null;
        }

        if (!videoSrc || typeof videoSrc !== 'string' || videoSrc.trim() === '') {
            console.error('[LSPlyr] Valid video source URL is required');
            this.handleInvalidUrl(videoElement, config);
            return null;
        }

        // Validate URL format
        try {
            new URL(videoSrc);
        } catch (error) {
            console.error('[LSPlyr] Invalid video URL format:', videoSrc);
            this.handleInvalidUrl(videoElement, config);
            return null;
        }

        if (Hls.isSupported()) {
            const hls = new Hls(this.config.hlsConfig);

            // Set up error handling before loading source
            hls.on(Hls.Events.ERROR, (event, data) => {
                console.error('[LSPlyr] HLS Error:', data);
                this.handleHlsError(hls, event, data, videoElement, config);
            });

            try {
                hls.loadSource(videoSrc);
                hls.attachMedia(videoElement);

                hls.on(Hls.Events.MANIFEST_PARSED, () => {
                    try {
                        const qualityOptions = this.generateQualityOptions(hls);
                        if (qualityOptions && qualityOptions.options && qualityOptions.options.length > 1) {
                            config.quality = {
                                default: qualityOptions.default,
                                options: qualityOptions.options,
                                forced: true,
                                onChange: qualityOptions.onChange,
                            };
                        }

                        const player = new Plyr(videoElement, config);

                        player.on('ready', () => {
                            const labels = qualityOptions?.labels;
                            if (labels) {
                                document.querySelectorAll('[data-plyr="quality"]').forEach((btn) => {
                                    const value = btn.getAttribute('value');
                                    if (labels.hasOwnProperty(value)) {
                                        const span = btn.querySelector('span');
                                        if (span) {
                                            span.textContent = labels[value];
                                        }
                                    }
                                });
                            }
                        });

                        if (player.quality) {
                            player.on('qualitychange', () => {
                                const currentLevel = player.quality;
                                if (hls && typeof currentLevel === 'number') {
                                    hls.currentLevel = currentLevel;
                                }
                            });
                        }

                        this.setupPlayerEvents(player, videoElement);
                        this.players.push({
                            element: videoElement,
                            player: player,
                            hls: hls,
                            isLive: true
                        });

                        const event = new CustomEvent('ls-player:initialized', {
                            detail: { player, element: videoElement, isLive: true }
                        });
                        document.dispatchEvent(event);

                    } catch (error) {
                        console.error('[LSPlyr] Error during manifest parsing:', error);
                        this.handlePlayerInitializationError(videoElement, config, error);
                    }
                });

                // Handle network errors and timeouts
                const timeoutId = setTimeout(() => {
                    console.warn('[LSPlyr] HLS loading timeout, continuing with other operations');
                }, 30000); // 30 second timeout

                hls.on(Hls.Events.MANIFEST_PARSED, () => {
                    clearTimeout(timeoutId);
                });

            } catch (error) {
                console.warn('[LSPlyr] Error initializing HLS, continuing with other operations');
            }

            return null; // Player will be created async

        } else if (videoElement.canPlayType('application/vnd.apple.mpegurl')) {
            // Native HLS support (Safari)
            try {
                videoElement.src = videoSrc;

                const handleError = (error) => {
                    console.warn('[LSPlyr] Native HLS error, continuing with other operations');
                };

                videoElement.addEventListener('error', handleError);

                videoElement.addEventListener('loadedmetadata', () => {
                    try {
                        const player = new Plyr(videoElement, config);
                        this.setupPlayerEvents(player, videoElement);
                        this.players.push({
                            element: videoElement,
                            player: player,
                            hls: null,
                            isLive: true
                        });

                        const event = new CustomEvent('ls-player:initialized', {
                            detail: { player, element: videoElement, isLive: true }
                        });
                        document.dispatchEvent(event);

                    } catch (error) {
                        console.warn('[LSPlyr] Error creating Plyr instance, continuing with other operations');
                    }
                });

                // Set a timeout for native HLS loading
                setTimeout(() => {
                    if (videoElement.readyState === 0) {
                        console.warn('[LSPlyr] Native HLS loading timeout, continuing with other operations');
                    }
                }, 30000);

            } catch (error) {
                console.warn('[LSPlyr] Error with native HLS, continuing with other operations');
            }

            return null;

        } else {
            console.warn('[LSPlyr] HLS is not supported in this browser');
            this.handleUnsupportedBrowser(videoElement, config);
            return null;
        }
    }

    /**
     * Handle invalid URL scenarios
     */
    handleInvalidUrl(videoElement, config) {
        // Silent handling - just log and continue
        console.warn('[LSPlyr] Invalid video URL, skipping initialization');
    }

    /**
     * Handle player initialization errors
     */
    handlePlayerInitializationError(videoElement, config, error) {
        // Silent handling - just log and continue
        console.warn('[LSPlyr] Player initialization failed, continuing with other operations');
    }

    /**
     * Handle unsupported browser scenario
     */
    handleUnsupportedBrowser(videoElement, config) {
        // Silent handling - just log and continue
        console.warn('[LSPlyr] HLS not supported, skipping this video element');
    }

    /**
     * Enhanced HLS error handler
     */
    handleHlsError(hls, event, data, videoElement, config) {
        console.warn('[LSPlyr] HLS Error:', {
            type: data.type,
            details: data.details,
            fatal: data.fatal,
            url: data.url
        });

        if (data.fatal) {
            switch (data.type) {
                case Hls.ErrorTypes.NETWORK_ERROR:
                    console.warn('[LSPlyr] Network error, skipping this video');
                    break;

                case Hls.ErrorTypes.MEDIA_ERROR:
                    console.warn('[LSPlyr] Media error, attempting recovery');
                    try {
                        hls.recoverMediaError();
                    } catch (recoveryError) {
                        console.warn('[LSPlyr] Could not recover from media error, continuing');
                    }
                    break;

                default:
                    console.warn('[LSPlyr] Fatal error, skipping this video');
                    break;
            }
        }
    }

    generateQualityOptions(hls) {
        const qualityOptions = [-1]; // Auto
        const qualityLabels = { '-1': 'Auto' };
        hls.levels.forEach((level, index) => {
            qualityOptions.push(index);

            const { height, bitrate } = level;
            let qualityName = '';

            if (height) {
                if (height >= 2160) qualityName = '4K';
                else if (height >= 1440) qualityName = '1440p';
                else if (height >= 1080) qualityName = '1080p';
                else if (height >= 720) qualityName = '720p HD';
                else if (height >= 480) qualityName = '480p';
                else if (height >= 360) qualityName = '360p';
                else qualityName = `${height}p`;

                if (bitrate) {
                    const mbps = Math.round(bitrate / 1000000 * 10) / 10;
                    qualityName += ` (${mbps} Mbps)`;
                }
            } else if (bitrate) {
                const mbps = Math.round(bitrate / 1000000 * 10) / 10;
                qualityName = `${mbps} Mbps`;
            } else {
                qualityName = `Level ${index + 1}`;
            }
            qualityLabels[index] = qualityName;
        });

        return {
            default: -1,
            options: qualityOptions,
            forced: true,
            labels: qualityLabels,
            onChange: (quality) => {
                hls.currentLevel = quality;
            }
        };
    }

    /**
     * Set up event handlers for player instances
     */
    setupPlayerEvents(player, element) {
        player.on('ready', () => {
            const event = new CustomEvent('ls-player:ready', {
                detail: { player, element }
            });
            element.dispatchEvent(event);
            document.dispatchEvent(event);

            // Initialize advertisement manager
            this.initializeAdManager(player, element);
        });

        player.on('play', () => {
            const event = new CustomEvent('ls-player:play', {
                detail: { player, element }
            });
            element.dispatchEvent(event);
            document.dispatchEvent(event);

            if (this.config.pauseOthers !== false) {
                this.pauseOtherPlayers(element);
            }
        });

        player.on('pause', () => {
            const event = new CustomEvent('ls-player:pause', {
                detail: { player, element }
            });
            element.dispatchEvent(event);
            document.dispatchEvent(event);
        });

        player.on('ended', () => {
            const event = new CustomEvent('ls-player:ended', {
                detail: { player, element }
            });
            element.dispatchEvent(event);
            document.dispatchEvent(event);
        });

        player.on('error', (error) => {
            const event = new CustomEvent('ls-player:error', {
                detail: { player, element, error }
            });
            element.dispatchEvent(event);
            document.dispatchEvent(event);
        });

        // For livestreams, add buffer monitoring
        if (element.getAttribute('data-is-live') === 'true' ||
            (element.getAttribute('data-hls-url')?.endsWith('.m3u8'))) {

            player.on('progress', () => {
                const playerItem = this.players.find(item => item.element === element);
                if (playerItem && playerItem.hls) {
                    const bufferLength = this.calculateBufferLength(playerItem.hls, player);

                    const bufferEvent = new CustomEvent('ls-player:buffer-update', {
                        detail: {
                            player,
                            element,
                            bufferLength,
                            healthy: bufferLength >= 3
                        }
                    });
                    element.dispatchEvent(bufferEvent);
                }
            });
        }
    }

    /**
     * Calculate buffer length for HLS streams
     */
    calculateBufferLength(hls, player) {
        if (hls.media.buffered.length > 0) {
            return hls.media.buffered.end(hls.media.buffered.length - 1) - player.currentTime;
        }
        return 0;
    }

    /**
     * Initialize advertisement manager for a player
     */
    initializeAdManager(player, element) {

        if (typeof window.LS_AdvertisementManager !== 'function') {
            // Retry mechanism for async script loading
            let retryCount = 0;
            const maxRetries = 3;

            const retryAdManagerInit = () => {
                retryCount++;
                if (typeof window.LS_AdvertisementManager === 'function') {
                    this.initializeAdManager(player, element);
                } else if (retryCount < maxRetries) {
                    setTimeout(retryAdManagerInit, 500);
                }
            };

            setTimeout(retryAdManagerInit, 500);
            return;
        }

        if (this.config.adConfig.enabled === false) {
            return;
        }

        const playerItem = this.players.find(item => item.element === element);
        if (!playerItem) return;

        const isLiveStream = playerItem.isLive || element.getAttribute('data-is-live') === 'true';
        const adConfig = this.buildAdConfig(element, isLiveStream);
        
        try {

            const adManager = new window.LS_AdvertisementManager(player, adConfig);

            playerItem.adManager = adManager;

            const event = new CustomEvent('ls-player:admanager-ready', {
                detail: { player, element, adManager }
            });
            element.dispatchEvent(event);
            document.dispatchEvent(event);
        } catch (error) {
            console.error('[LSPlyr] Failed to initialize advertisement manager:', error);
        }
    }

    /**
     * Build ad configuration from element attributes and config
     */
    buildAdConfig(element, isLiveStream) {

        const htmlAds = this.parseHtmlAds(element);
        const vastAdUrls = this.parseVastUrls(element);

        // Check for legacy VAST URL attributes
        this.parseLegacyVastUrls(element, vastAdUrls);

        return {
            isLiveStream,
            adsType: element.getAttribute('data-ads-type') || 'vast',
            prerollEnabled: this.parseBoolean(element.getAttribute('data-preroll-enabled'),
                this.config.adConfig.prerollEnabled),
            midrollEnabled: this.parseBoolean(element.getAttribute('data-midroll-enabled'),
                this.config.adConfig.midrollEnabled),
            postrollEnabled: this.parseBoolean(element.getAttribute('data-postroll-enabled'),
                this.config.adConfig.postrollEnabled),
            adFrequency: parseInt(element.getAttribute('data-ad-frequency')) ||
                this.config.adConfig.adFrequency || 5,
            vastAdUrls: this.mergeAdUrls(vastAdUrls, this.config.adConfig.vastAdUrls),
            htmlAds: this.mergeAdUrls(htmlAds, this.config.adConfig.htmlAds),
            imaSettings: this.config.adConfig.imaSettings || {}
        };
    }

    /**
     * Parse boolean attribute with default fallback
     */
    parseBoolean(value, defaultValue) {
        if (value === 'true') return true;
        if (value === 'false') return false;
        return defaultValue !== false;
    }

    /**
     * Parse legacy VAST URL attributes
     */
    parseLegacyVastUrls(element, vastAdUrls) {
        const mappings = {
            'data-vast-url': 'any',
            'data-vast-preroll': 'preroll',
            'data-vast-midroll': 'midroll',
            'data-vast-postroll': 'postroll'
        };

        Object.entries(mappings).forEach(([attr, type]) => {
            const url = element.getAttribute(attr);
            if (url && url.trim()) {
                vastAdUrls[type] = vastAdUrls[type] || [];
                vastAdUrls[type].push(url.trim());
            }
        });
    }

    /**
     * Merge ad URLs from different sources
     */
    mergeAdUrls(parsed, config) {

        const merged = {};
        ['preroll', 'midroll', 'postroll', 'any'].forEach(type => {
            merged[type] = [
                ...(parsed[type] || []),
                ...(config[type] || [])
            ];
        });
        
        return merged;
    }

    /**
     * Parse HTML ads from element data attribute
     */
    parseHtmlAds(element) {
        const htmlAds = {
            preroll: [],
            midroll: [],
            postroll: [],
            any: []
        };

        try {
            const htmlAdsAttr = element.getAttribute('data-html-ads');

            if (htmlAdsAttr) {
                const adsData = JSON.parse(htmlAdsAttr);
                this.processHtmlAdsData(adsData, htmlAds);
            }
            // Check for global ad settings
            if (window.lsAdSettings?.htmlAds) {
                this.processHtmlAdsData(window.lsAdSettings.htmlAds, htmlAds);
            }
        } catch (error) {
            console.error('[LSPlyr] Error parsing HTML ads:', error);
        }

        return htmlAds;
    }

    /**
     * Process HTML ads data
     */
    processHtmlAdsData(adsData, htmlAds) {
        ['preroll', 'midroll', 'postroll', 'any'].forEach(adPosition => {

            if (Array.isArray(adsData[adPosition])) {
                adsData[adPosition].forEach(ad => {
                    const processedAd = this.processHtmlAd(ad, adPosition);
                    if (processedAd) {
                        htmlAds[adPosition].push(processedAd);
                    }
                });
            }
        });
    }

    /**
     * Process individual HTML ad
     */
    processHtmlAd(ad, adPosition) {
        const baseAd = {
            duration: ad.duration || 15,
            campaign: ad.campaign || `${adPosition}-${ad.type}`,
            skipenabled: ad.skipenabled || false,
            skipDuration: ad.skipDuration,
            position: ad.position || 'bottom-right'
        };

        if (ad.type === 'video' || (ad.type === 'full_screen' && ad.videoUrl)) {
            return {
                ...baseAd,
                type: 'video',
                videoUrl: ad.videoUrl || ad.url,
                fullScreen: true,
                adFormat: 'fullscreen',
                overlay: false
            };
        } else if (ad.type === 'companion') {
            return {
                ...baseAd,
                type: 'html',
                content: ad.content,
                fullScreen: false,
                adFormat: 'companion',
                overlay: false
            };
        } else if (ad.type === 'overlay') {
            return {
                ...baseAd,
                type: 'html',
                content: ad.content,
                fullScreen: false,
                adFormat: 'overlay',
                overlay: true,
                duration: ad.duration || 30
            };
        } else if (ad.type === 'full_screen' || ad.type === 'html') {
            return {
                ...baseAd,
                type: 'html',
                content: ad.content || ad.html || '',
                fullScreen: ad.fullScreen !== false,
                adFormat: ad.adFormat || 'fullscreen',
                overlay: ad.overlay === true
            };
        }

        return null;
    }

    /**
     * Parse VAST ad URLs from element data attribute
     */
    parseVastUrls(element) {
        const vastUrls = {
            preroll: [],
            midroll: [],
            postroll: [],
            any: []
        };

        try {
            const vastUrlsAttr = element.getAttribute('data-vast-urls');
            if (vastUrlsAttr) {
                const urlsData = JSON.parse(vastUrlsAttr);
                this.processVastUrlsData(urlsData, vastUrls);
            }

            // Check for global ad settings
            if (window.lsAdSettings?.vastAdUrls) {
                this.processVastUrlsData(window.lsAdSettings.vastAdUrls, vastUrls);
            }
        } catch (error) {
            console.error('[LSPlyr] Error parsing VAST URLs:', error);
        }
        
        return vastUrls;
    }

    /**
     * Process VAST URLs data
     */
    processVastUrlsData(urlsData, vastUrls) {
        ['preroll', 'midroll', 'postroll', 'any'].forEach(type => {
            if (Array.isArray(urlsData[type])) {
                urlsData[type].forEach(item => {
                    if (typeof item === 'string') {
                        vastUrls[type].push(item);
                    } else if (item?.url) {
                        vastUrls[type].push(item.url);
                    }
                });
            } else if (typeof urlsData[type] === 'string') {
                vastUrls[type].push(urlsData[type]);
            }
        });
    }

    /**
     * Deep merge multiple objects
     */
    mergeDeep(target, ...sources) {
        if (!sources.length) return target;

        const isObject = item => (item && typeof item === 'object' && !Array.isArray(item));
        const source = sources.shift();

        if (isObject(target) && isObject(source)) {
            for (const key in source) {
                if (isObject(source[key])) {
                    if (!target[key]) Object.assign(target, { [key]: {} });
                    this.mergeDeep(target[key], source[key]);
                } else {
                    Object.assign(target, { [key]: source[key] });
                }
            }
        }

        return this.mergeDeep(target, ...sources);
    }

    /**
     * Handle HLS.js errors
     */
    handleHlsError(hls, event, data) {
        if (data.fatal) {
            switch (data.type) {
                case Hls.ErrorTypes.NETWORK_ERROR:
                    console.warn('[LSPlyr] Network error, trying to recover:', data);
                    hls.startLoad();
                    break;
                case Hls.ErrorTypes.MEDIA_ERROR:
                    console.warn('[LSPlyr] Media error, trying to recover:', data);
                    hls.recoverMediaError();
                    break;
                default:
                    console.error('[LSPlyr] Unrecoverable error:', data);
                    hls.destroy();
                    break;
            }
        } else {
            console.warn('[LSPlyr] HLS.js non-fatal error:', data);
        }
    }

    /**
     * Pause all other players when one starts playing
     */
    pauseOtherPlayers(exceptElement) {
        this.players.forEach(item => {
            if (item.element !== exceptElement && item.player?.pause) {
                item.player.pause();
            }
        });
    }

    /**
     * Set up global event handlers
     */
    setupEventHandlers() {
        document.addEventListener('ls-player:request-pause-all', () => {
            this.players.forEach(item => {
                if (item.player?.pause) {
                    item.player.pause();
                }
            });
        });
    }

    /**
     * Destroy the LSPlyr instance
     */
    destroy() {
        if (this.observer) {
            this.observer.disconnect();
        }

        this.players.forEach(item => {
            if (item.player) {
                item.player.destroy();
            }
            if (item.hls) {
                item.hls.destroy();
            }
            if (item.adManager) {
                item.adManager.destroy();
            }
        });

        this.players = [];
    }
}

/**
 * Static method to create a singleton instance
 */
LSPlyr.create = function (config = {}) {
    if (!window.LSPlyrInstance) {
        window.LSPlyrInstance = new LSPlyr(config);
    }
    return window.LSPlyrInstance;
};

// Initialize as a global singleton
document.addEventListener('DOMContentLoaded', () => {
    const lsPlyr = LSPlyr.create();

    // Ensure ls_video_player is initialized if present
    const lsVideoPlayer = document.getElementById('ls_video_player');
    if (lsVideoPlayer && !lsVideoPlayer.classList.contains('ls-player-initialized')) {
        lsPlyr.initSinglePlayer(lsVideoPlayer);
    }
});

// Export for module usage
export default LSPlyr;